
import { mapState, mapGetters } from 'vuex';
import hasStickyData from '..//../mixins/hasStickyData';

export default {
  title: 'NavigationSidebar',
  data() {
    return {
      menuExpanded: false,
      contractedMenu: '',
      expandedMenu: '',
      contracted: {},

      navigationItems: [
        {
          to: '/dashboard',
          icon: 'fa-home',
          text: 'Dashboard',
          children: [
            {
              to: '/dashboard',
              text: 'My Overview',
              restricted: [],
            },
            {
              to: '/dashboard/my-logged-time',
              text: 'My Logged time',
              restricted: [],
            },
            {
              to: '/dashboard/my-tasks',
              text: 'My Tasks',
              restricted: [],
            },
            {
              to: '/dashboard/my-forms',
              text: 'My Forms',
              restricted: [],
            },
            {
              to: '/dashboard/upcoming-reviews',
              text: 'My Reviews',
              restricted: [],
            },
          ],
          restricted: [],
        },
        {
          to: '/contacts',
          icon: 'fa-users',
          text: 'Contacts',
          children: [
            {
              to: '/contacts',
              text: 'Contacts',
              restricted: [],
            },
            {
              to: '/circle',
              text: 'Circles',
              restricted: [],
            },
            {
              to: '/plans',
              text: 'Plans',
              restricted: [],
            },
          ],
          restricted: [],
        },
        {
          to: '/cases',
          icon: 'fa-briefcase',
          text: 'Cases',
          restricted: [],
        },
        {
          to: '/referrals',
          icon: 'fa-handshake',
          text: 'Referrals',
          restricted: ['feature:referrals'],
          children: [
            {
              to: '/referrals',
              text: 'My Referrals',
              restricted: ['feature:referrals'],
            },
            {
              to: '/referrals/connections',
              text: 'Connections',
              restricted: ['feature:referrals'],
            },
          ],
        },
        {
          to: '/business',
          icon: 'fa-building',
          text: 'Business',
          children: [
            {
              to: '/business/key-metrics',
              text: 'Key Metrics',
              restricted: ['owners', 'admins'],
            },
            {
              to: '/automations',
              text: 'Automations',
              restricted: [],
            },
            {
              to: '/business/docs',
              text: 'Document Builder',
              restricted: [],
            },
            {
              to: '/business/forms',
              text: 'Form Builder',
              restricted: [],
            },
            {
              to: '/business/signatures',
              text: 'Signature Requests',
              restricted: [],
            },
            {
              to: '/business/income-reconciliation',
              text: 'Income Reconciliation',
              restricted: ['owners', 'admins'],
            },
            {
              to: '/business/reporting',
              text: 'Reporting / Exports',
              restricted: ['owners', 'admins'],
            },
            {
              to: '/business/imports',
              text: 'Imports',
              restricted: ['owners', 'admins'],
            },
            {
              to: '/business/pensionlab',
              text: 'The PensionLab',
              restricted: ['feature:pensionlab-addon', 'addon:pensionlab'],
            },
          ],
          restricted: [],
        },
        {
          to: '/settings',
          icon: 'fa-gear',
          text: 'Settings',
          restricted: [],
        },
      ],
    };
  },
  mixins: [hasStickyData],
  computed: {
    ...mapState('navigation', ['clickInteraction']),
    ...mapState('user', ['enabledFeatures']),
    ...mapGetters('user', [
      'isPortalUser',
      'isClient',
      'isEmployee',
      'isExternal',
      'isOwner',
      'isAdmin',
      'isMember',
      'isNotSeniorEmployee',
      'hasEnabledAddon',
    ]),

    navigation() {
      return this.navigationItems.map((item) => this.filterAndClone(item)).filter(Boolean);
    },
  },

  mounted() {
    this.$nextTick(async () => {
      const menuCollapsed = this.$route?.query?.menuCollapsed;

      if (menuCollapsed !== undefined) {
        // URL param takes precedence — read synchronously before any await
        if (menuCollapsed === 'true') {
          this.closeMenu();
        } else {
          this.openMenu();
        }
      } else {
        // No URL param — fall back to saved preference
        const isOpen = await this.getStickyData('menuOpen');

        if (isOpen === true) {
          this.openMenu();
        } else {
          this.closeMenu();
        }
      }
    });
  },

  methods: {
    widowResize() {
      this.setInitialStates();
    },
    async setInitialStates() {
      // Check if there's a menuCollapsed query parameter
      const menuCollapsed = this.$route?.query?.menuCollapsed;

      if (menuCollapsed === 'true') {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    },

    openMenu() {
      this.menuExpanded = true;
      this.setStickyData('menuOpen', true);
      const element = document.getElementById('__layout');
      if (element) {
        element.classList.add('open');
        element.classList.remove('closed');
      }
    },

    closeMenu() {
      this.menuExpanded = false;
      this.setStickyData('menuOpen', false);
      const element = document.getElementById('__layout');
      if (element) {
        element.classList.add('closed');
        element.classList.remove('open');
      }
    },

    switchMenu() {
      if (this.menuExpanded) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    },

    hasPensionLab() {
      return this.hasEnabledAddon('pensionlab');
    },

    hasOpenwork() {
      return this.hasEnabledAddon('openwork');
    },

    hasFeature(feature) {
      if (!feature || typeof feature !== 'string') return false;
      const name = feature.startsWith('feature:') ? feature.slice('feature:'.length) : feature;
      if (name.toLowerCase().includes('pensionlab')) {
        return this.hasPensionLab() || this.enabledFeatures.includes(name);
      }
      if (name.toLowerCase().includes('openwork')) {
        return this.hasOpenwork() || this.enabledFeatures.includes(name);
      }
      return this.enabledFeatures.includes(name);
    },

    // Returns a cloned item (with filtered children) or null if item should be excluded.
    filterAndClone(item) {
      if (item.hasOwnProperty('show') && !item.show) return null;

      const clone = Object.assign({}, item);

      if (Array.isArray(clone.children)) {
        clone.children = clone.children.map((child) => this.filterAndClone(child)).filter(Boolean);
      }

      if (!clone.restricted || !Array.isArray(clone.restricted)) {
        return clone;
      }

      if (clone.restricted.some((r) => ['owners', 'admins'].includes(r)) && this.isNotSeniorEmployee) {
        return null;
      }

      const addonRestrictions = clone.restricted.filter((r) => typeof r === 'string' && r.startsWith('addon:'));
      if (addonRestrictions.length > 0) {
        const ok = addonRestrictions.every((ar) => this.hasEnabledAddon(ar.slice('addon:'.length)));
        if (!ok) return null;
      }

      const featureRestrictions = clone.restricted.filter((r) => typeof r === 'string' && r.startsWith('feature:'));
      if (featureRestrictions.length > 0) {
        const ok = featureRestrictions.every((fr) => this.hasFeature(fr));
        if (!ok) return null;
      }

      return clone;
    },
  },
};
